import { useState, useEffect } from 'react';
import { 
  AlertTriangle, Check, ChevronDown, ChevronUp, Info, Save, 
  X, HelpCircle, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Skill Structure
const SKILL_STRUCTURE = {
  technical: {
    name: "Technical Skills",
    skills: ["ball_control", "passing", "dribbling", "first_touch", "finishing", "shooting"]
  },
  physical: {
    name: "Physical Performance",
    skills: ["speed", "acceleration", "agility", "coordination", "balance", "explosiveness", "endurance"]
  },
  game_intelligence: {
    name: "Game Intelligence",
    skills: ["positioning", "decision_making", "vision", "game_awareness", "reaction_time"]
  }
};

const SKILL_NAMES = {
  ball_control: "Ball Control",
  passing: "Passing",
  dribbling: "Dribbling",
  first_touch: "First Touch",
  finishing: "Finishing",
  shooting: "Shooting",
  speed: "Speed",
  acceleration: "Acceleration",
  agility: "Agility",
  coordination: "Coordination",
  balance: "Balance",
  explosiveness: "Explosiveness",
  endurance: "Endurance",
  positioning: "Positioning",
  decision_making: "Decision Making",
  vision: "Vision",
  game_awareness: "Game Awareness",
  reaction_time: "Reaction Time"
};

const LEVELS = [
  { value: "focus", label: "Focus", color: "bg-red-500/20 text-red-400 border-red-500/50" },
  { value: "developing", label: "Developing", color: "bg-amber-500/20 text-amber-400 border-amber-500/50" },
  { value: "proficient", label: "Proficient", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/50" }
];

const LEVEL_DESCRIPTIONS = {
  focus: "Skill that is limiting performance or requires clear intervention.",
  developing: "Skill in evolution, still inconsistent, but not a critical problem.",
  proficient: "Skill consistent for the athlete's current level."
};

/**
 * PlayerEvaluationForm
 * Complete evaluation form with consistency checking
 */
const PlayerEvaluationForm = ({ 
  athleteId, 
  athleteName,
  coachId, 
  coachName,
  coachType = "private", // 'private' or 'team'
  existingEvaluation = null,
  onSave,
  onCancel 
}) => {
  const [skills, setSkills] = useState({});
  const [generalNotes, setGeneralNotes] = useState('');
  const [sessionContext, setSessionContext] = useState('training');
  const [expandedCategories, setExpandedCategories] = useState(['technical', 'physical', 'game_intelligence']);
  const [loading, setLoading] = useState(false);
  const [consistencyResult, setConsistencyResult] = useState(null);
  const [showConsistencyModal, setShowConsistencyModal] = useState(false);
  const [showLevelHelp, setShowLevelHelp] = useState(false);

  // Initialize from existing evaluation
  useEffect(() => {
    if (existingEvaluation) {
      const skillMap = {};
      existingEvaluation.skills?.forEach(s => {
        skillMap[s.skill] = { level: s.level, note: s.note || '' };
      });
      setSkills(skillMap);
      setGeneralNotes(existingEvaluation.general_notes || '');
      setSessionContext(existingEvaluation.session_context || 'training');
    }
  }, [existingEvaluation]);

  const updateSkill = (skill, field, value) => {
    setSkills(prev => ({
      ...prev,
      [skill]: {
        ...prev[skill],
        [field]: value
      }
    }));
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const buildEvaluationData = () => {
    const skillsList = Object.entries(skills)
      .filter(([_, data]) => data.level)
      .map(([skill, data]) => ({
        skill,
        level: data.level,
        note: data.note || null
      }));

    return {
      athlete_id: athleteId,
      coach_id: coachId,
      coach_type: coachType,
      coach_name: coachName,
      skills: skillsList,
      general_notes: generalNotes,
      session_context: sessionContext
    };
  };

  const handleCheckConsistency = async () => {
    const data = buildEvaluationData();
    
    if (data.skills.length === 0) {
      toast.error('Please evaluate at least one skill');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/evaluations/check-consistency`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      const result = await response.json();
      setConsistencyResult(result);

      if (result.has_alerts) {
        setShowConsistencyModal(true);
      } else {
        // No alerts, save directly
        await saveEvaluation(false);
      }
    } catch (error) {
      console.error('Consistency check error:', error);
      toast.error('Failed to check consistency');
    } finally {
      setLoading(false);
    }
  };

  const saveEvaluation = async (forceSave = false) => {
    const data = buildEvaluationData();
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/evaluations/?force_save=${forceSave}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      const result = await response.json();

      if (result.status === 'consistency_check_failed') {
        setConsistencyResult(result.consistency);
        setShowConsistencyModal(true);
        return;
      }

      if (result.status === 'saved') {
        toast.success('Evaluation saved successfully');
        setShowConsistencyModal(false);
        onSave?.(result);
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save evaluation');
    } finally {
      setLoading(false);
    }
  };

  const getCompletionStats = () => {
    const totalSkills = Object.values(SKILL_STRUCTURE).reduce((acc, cat) => acc + cat.skills.length, 0);
    const evaluatedSkills = Object.values(skills).filter(s => s.level).length;
    return { total: totalSkills, evaluated: evaluatedSkills, percentage: Math.round((evaluatedSkills / totalSkills) * 100) };
  };

  const stats = getCompletionStats();

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-heading">
              {coachType === 'private' ? 'Private Coach' : 'Team Coach'} Evaluation
            </h2>
            <p className="text-sm text-muted-foreground">
              Evaluating: <span className="text-foreground font-medium">{athleteName}</span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Completion</p>
              <p className="text-lg font-bold text-primary">{stats.percentage}%</p>
            </div>
            <button 
              onClick={() => setShowLevelHelp(!showLevelHelp)}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
              title="Level definitions"
            >
              <HelpCircle size={20} className="text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Level Help */}
        {showLevelHelp && (
          <div className="mt-4 p-4 bg-background rounded-lg border border-border">
            <h3 className="font-medium mb-3">Skill Levels</h3>
            <div className="space-y-2">
              {LEVELS.map(level => (
                <div key={level.value} className="flex items-start gap-3">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${level.color}`}>
                    {level.label}
                  </span>
                  <p className="text-sm text-muted-foreground">{LEVEL_DESCRIPTIONS[level.value]}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Session Context */}
      <div className="p-4 border-b border-border">
        <label className="text-sm font-medium mb-2 block">Evaluation Context</label>
        <div className="flex gap-2">
          {['training', 'match', 'assessment'].map(ctx => (
            <button
              key={ctx}
              onClick={() => setSessionContext(ctx)}
              className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                sessionContext === ctx 
                  ? 'bg-primary text-black' 
                  : 'bg-muted hover:bg-muted/80'
              }`}
            >
              {ctx.charAt(0).toUpperCase() + ctx.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Skills */}
      <div className="divide-y divide-border">
        {Object.entries(SKILL_STRUCTURE).map(([categoryKey, category]) => (
          <div key={categoryKey}>
            {/* Category Header */}
            <button
              onClick={() => toggleCategory(categoryKey)}
              className="w-full p-4 flex items-center justify-between hover:bg-muted/30 transition-colors"
            >
              <div className="flex items-center gap-3">
                <h3 className="font-medium">{category.name}</h3>
                <span className="text-xs text-muted-foreground">
                  {category.skills.filter(s => skills[s]?.level).length}/{category.skills.length} evaluated
                </span>
              </div>
              {expandedCategories.includes(categoryKey) ? (
                <ChevronUp size={20} className="text-muted-foreground" />
              ) : (
                <ChevronDown size={20} className="text-muted-foreground" />
              )}
            </button>

            {/* Skills in Category */}
            {expandedCategories.includes(categoryKey) && (
              <div className="px-4 pb-4 space-y-4">
                {category.skills.map(skill => (
                  <div key={skill} className="p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-medium">{SKILL_NAMES[skill]}</span>
                      <div className="flex gap-1">
                        {LEVELS.map(level => (
                          <button
                            key={level.value}
                            onClick={() => updateSkill(skill, 'level', level.value)}
                            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                              skills[skill]?.level === level.value
                                ? level.color + ' ring-2 ring-offset-2 ring-offset-background'
                                : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                            }`}
                          >
                            {level.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <input
                      type="text"
                      placeholder="Add observation (optional)..."
                      value={skills[skill]?.note || ''}
                      onChange={(e) => updateSkill(skill, 'note', e.target.value)}
                      className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* General Notes */}
      <div className="p-4 border-t border-border">
        <label className="text-sm font-medium mb-2 block">General Notes</label>
        <textarea
          placeholder="Overall observations about this evaluation..."
          value={generalNotes}
          onChange={(e) => setGeneralNotes(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-primary resize-none"
        />
      </div>

      {/* Actions */}
      <div className="p-4 bg-muted/30 border-t border-border flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {stats.evaluated} of {stats.total} skills evaluated
        </p>
        <div className="flex gap-3">
          {onCancel && (
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button 
            onClick={handleCheckConsistency}
            disabled={loading || stats.evaluated === 0}
            className="bg-primary text-black"
          >
            {loading ? (
              <>Checking...</>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Evaluation
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Consistency Check Modal */}
      {showConsistencyModal && consistencyResult && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg max-w-lg w-full max-h-[80vh] overflow-auto">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="text-amber-400" size={20} />
                <h2 className="font-heading">Evaluation Consistency Check</h2>
              </div>
              <button onClick={() => setShowConsistencyModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <p className="text-sm text-muted-foreground">
                We detected {consistencyResult.alerts?.length || 0} potential inconsistencies in your evaluation.
              </p>

              {/* Reliability Badge */}
              <div className="flex items-center gap-2">
                <span className="text-sm">Evaluation Reliability:</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  consistencyResult.reliability === 'high' 
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : consistencyResult.reliability === 'medium'
                    ? 'bg-amber-500/20 text-amber-400'
                    : 'bg-red-500/20 text-red-400'
                }`}>
                  {consistencyResult.reliability?.toUpperCase()}
                </span>
              </div>

              {/* Alerts */}
              <div className="space-y-3">
                {consistencyResult.alerts?.map((alert, idx) => (
                  <div 
                    key={idx}
                    className={`p-3 rounded-lg border ${
                      alert.severity === 'warning' 
                        ? 'bg-amber-500/10 border-amber-500/30'
                        : 'bg-blue-500/10 border-blue-500/30'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {alert.severity === 'warning' ? (
                        <AlertTriangle size={16} className="text-amber-400 mt-0.5" />
                      ) : (
                        <Info size={16} className="text-blue-400 mt-0.5" />
                      )}
                      <div>
                        <p className="text-sm">{alert.message}</p>
                        {alert.suggestion && (
                          <p className="text-xs text-muted-foreground mt-1">{alert.suggestion}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 border-t border-border flex gap-3 justify-end">
              <Button 
                variant="outline" 
                onClick={() => setShowConsistencyModal(false)}
              >
                Review Evaluation
              </Button>
              <Button 
                onClick={() => saveEvaluation(true)}
                disabled={loading}
                className="bg-primary text-black"
              >
                {loading ? 'Saving...' : 'Save Anyway'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerEvaluationForm;
